import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Sparkles, Loader2, ChevronRight } from 'lucide-react';
import {
  TeamBApi,
  ApiError,
  type Plant,
  type StageReportResult,
  type StageReportListItem,
  formatDateTime,
} from '@ai-tiaozs/shared';
import { StageReportDetail } from '@/components/StageReportDetail';

/**
 * B 队 · 家庭植物健康报告
 * 历史列表 + 详情折叠 + 相同记录集去重
 */
export function TeamBStageReport() {
  const [searchParams] = useSearchParams();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [plantId, setPlantId] = useState<string>('');
  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [history, setHistory] = useState<StageReportListItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<StageReportResult | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [duplicateHint, setDuplicateHint] = useState<StageReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async (pid: string) => {
    setLoadingHistory(true);
    try {
      const { reports } = await TeamBApi.listReports(pid);
      setHistory(reports);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    TeamBApi.listPlants()
      .then((d) => {
        setPlants(d.plants);
        const fromUrl = searchParams.get('plantId');
        const initial =
          fromUrl && d.plants.some((p) => p.id === fromUrl) ? fromUrl : d.plants[0]?.id ?? '';
        setPlantId(initial);
      })
      .catch(() => setPlants([]));
  }, [searchParams]);

  useEffect(() => {
    if (!plantId) {
      setRecordCount(null);
      setHistory([]);
      return;
    }
    TeamBApi.timeline(plantId)
      .then((t) => setRecordCount(t.totalDays))
      .catch(() => setRecordCount(null));
    void loadHistory(plantId);
    setResult(null);
    setSelectedReportId(null);
    setDuplicateHint(null);
  }, [plantId, loadHistory]);

  const openReport = async (reportId: string) => {
    setError(null);
    setSelectedReportId(reportId);
    try {
      const r = await TeamBApi.getReport(reportId);
      setResult(r);
      setDuplicateHint(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载报告失败');
    }
  };

  const submit = async (force = false) => {
    if (!plantId) {
      setError('请先建一株植物');
      return;
    }
    if (recordCount === 0) {
      setError('这株植物还没有拍照记录,请先去记录页拍一张');
      return;
    }
    setLoading(true);
    setError(null);
    if (!force) {
      setDuplicateHint(null);
    }
    try {
      const r = await TeamBApi.stageReport({ plantId, force: force || undefined });
      if (r.duplicate && !force) {
        setDuplicateHint(r);
        setResult(r);
        setSelectedReportId(r.reportId);
      } else {
        setResult(r);
        setSelectedReportId(r.reportId);
        setDuplicateHint(null);
        await loadHistory(plantId);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '生成失败');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlant = plants.find((p) => p.id === plantId);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">📊 家庭植物健康报告</h1>
        <p className="text-xs text-slate-500">
          自动整理拍照记录生成 AI 总结,历史报告保存在下方列表
        </p>
      </header>

      <section className="card space-y-3">
        <div>
          <label className="text-xs text-slate-500">选择植物</label>
          {plants.length === 0 ? (
            <p className="mt-1 text-xs text-teamA-danger">
              还没有植物,先去
              <Link to="/" className="ml-1 text-teamB-primary underline">
                首页
              </Link>
              建一个档案。
            </p>
          ) : (
            <select
              className="input mt-1"
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
            >
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.location}
                </option>
              ))}
            </select>
          )}
        </div>

        {plantId && recordCount != null && (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {recordCount > 0
              ? `将汇总「${selectedPlant?.name ?? '该植物'}」的全部 ${recordCount} 条拍照记录。相同记录集不会重复生成。`
              : '暂无拍照记录,请先去「记录」页拍一张再生成报告。'}
          </p>
        )}

        {duplicateHint && (
          <div className="rounded-md border border-teamA-warning/40 bg-teamA-warning/10 px-3 py-2 text-xs text-slate-700">
            <p>
              这批 {duplicateHint.observationCount} 条记录已在{' '}
              {formatDateTime(duplicateHint.createdAt)} 生成过报告。
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className="btn-primary flex-1 py-1.5 text-xs"
                onClick={() => void openReport(duplicateHint.reportId)}
              >
                查看已有报告
              </button>
              <button
                type="button"
                className="btn-outline flex-1 py-1.5 text-xs"
                onClick={() => void submit(true)}
                disabled={loading}
              >
                仍要重新生成
              </button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-teamA-danger">{error}</p>}
        <button
          onClick={() => void submit(false)}
          disabled={loading || plants.length === 0 || recordCount === 0}
          className="btn-primary w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" /> AI 正在整理报告…
            </>
          ) : (
            <>
              <Sparkles className="mr-1 h-4 w-4" /> 生成报告
            </>
          )}
        </button>
      </section>

      {plantId && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-700">
            📋 {selectedPlant?.name ?? '植物'} 的历史报告
          </h2>
          {loadingHistory && (
            <p className="flex items-center gap-1 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> 加载历史…
            </p>
          )}
          {!loadingHistory && history.length === 0 && (
            <p className="card text-xs text-slate-500">还没有报告,点上方生成第一份。</p>
          )}
          <ul className="space-y-2">
            {history.map((item) => {
              const active = selectedReportId === item.reportId;
              const span =
                item.timeStart && item.timeEnd
                  ? `${formatDateTime(item.timeStart).slice(0, 10)} — ${formatDateTime(item.timeEnd).slice(0, 16)}`
                  : '';
              return (
                <li key={item.reportId}>
                  <button
                    type="button"
                    onClick={() => void openReport(item.reportId)}
                    className={`card w-full text-left transition ${
                      active ? 'ring-2 ring-teamB-primary/50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1 text-sm font-medium text-slate-800">
                          <FileText className="h-3.5 w-3.5 shrink-0 text-teamB-primary" />
                          {formatDateTime(item.createdAt)}
                          <span className="text-xs font-normal text-slate-500">
                            · 共 {item.recordCount} 条
                          </span>
                        </p>
                        {span && <p className="mt-0.5 text-[11px] text-slate-400">{span}</p>}
                        <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                          {item.summaryPreview || '—'}
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {item.mismatchCount > 0 && (
                            <span className="badge bg-teamA-warning/15 text-[10px] text-teamA-warning">
                              ⚠️ {item.mismatchCount} 条错挂
                            </span>
                          )}
                          {item.hasAbnormalEvents && (
                            <span className="badge bg-teamA-danger/10 text-[10px] text-teamA-danger">
                              有异常
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {result && <StageReportDetail report={result} />}

      <Link to={`/timeline?plantId=${plantId}`} className="btn-outline w-full">
        返回时间线
      </Link>
    </div>
  );
}
